import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { TrendingUp, TrendingDown, Users, BookOpen, Award, Target } from "lucide-react";

interface StudentPerformance {
  student_email: string;
  student_name: string | null;
  total_tests: number;
  average_score: number;
  subjects: string[];
  recent_scores: number[];
  improvement_trend: 'up' | 'down' | 'stable';
  last_test_date: string | null;
}

interface TestSession {
  id: string;
  user_id: string;
  subject_id: string;
  score: number | null;
  total_questions: number;
  completed: boolean;
  created_at: string;
  grade: string;
  subjects: {
    name: string;
  };
}

export default function InstructorStudentAnalysis() {
  const { user } = useAuth();
  const [studentPerformances, setStudentPerformances] = useState<StudentPerformance[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [testSessions, setTestSessions] = useState<TestSession[]>([]);

  useEffect(() => {
    loadStudentAnalysis();
  }, [user]);

  const loadStudentAnalysis = async () => {
    if (!user) return;

    try {
      // Get instructor's students
      const { data: studentEmails } = await supabase
        .from('instructor_student_emails')
        .select(`
          email,
          student_name,
          instructor_student_lists!inner(instructor_id)
        `)
        .eq('instructor_student_lists.instructor_id', user.id);

      if (!studentEmails || studentEmails.length === 0) {
        setLoading(false);
        return;
      }

      const studentEmailList = studentEmails.map(s => s.email);

      // Get test sessions for these students
      const { data: sessions, error } = await supabase
        .from('test_sessions')
        .select(`
          *,
          subjects(name)
        `)
        .in('user_id', studentEmailList)
        .eq('completed', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setTestSessions(sessions || []);

      // Process performance data
      const performanceMap = new Map<string, StudentPerformance>();

      studentEmails.forEach(student => {
        const studentSessions = sessions?.filter(s => s.user_id === student.email) || [];
        
        const scores = studentSessions
          .filter(s => s.score !== null)
          .map(s => (s.score! / s.total_questions) * 100);

        const subjects = [...new Set(studentSessions.map(s => s.subjects?.name).filter(Boolean))];
        
        const averageScore = scores.length > 0 
          ? scores.reduce((a, b) => a + b, 0) / scores.length 
          : 0;

        // Calculate trend (last 5 vs previous 5 scores)
        const recentScores = scores.slice(0, 5);
        const previousScores = scores.slice(5, 10);
        
        let trend: 'up' | 'down' | 'stable' = 'stable';
        if (recentScores.length >= 3 && previousScores.length >= 3) {
          const recentAvg = recentScores.reduce((a, b) => a + b, 0) / recentScores.length;
          const previousAvg = previousScores.reduce((a, b) => a + b, 0) / previousScores.length;
          
          if (recentAvg > previousAvg + 5) trend = 'up';
          else if (recentAvg < previousAvg - 5) trend = 'down';
        }

        performanceMap.set(student.email, {
          student_email: student.email,
          student_name: student.student_name,
          total_tests: studentSessions.length,
          average_score: averageScore,
          subjects,
          recent_scores: scores.slice(0, 10).reverse(), // Last 10 scores in chronological order
          improvement_trend: trend,
          last_test_date: studentSessions[0]?.created_at || null
        });
      });

      setStudentPerformances(Array.from(performanceMap.values()));
    } catch (error) {
      console.error('Error loading student analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPerformances = selectedStudent === 'all' 
    ? studentPerformances 
    : studentPerformances.filter(p => p.student_email === selectedStudent);

  const getOverallStats = () => {
    if (studentPerformances.length === 0) return null;

    const totalTests = studentPerformances.reduce((sum, p) => sum + p.total_tests, 0);
    const averageScore = studentPerformances.reduce((sum, p) => sum + p.average_score, 0) / studentPerformances.length;
    const improvingStudents = studentPerformances.filter(p => p.improvement_trend === 'up').length;
    const activeStudents = studentPerformances.filter(p => p.total_tests > 0).length;

    return { totalTests, averageScore, improvingStudents, activeStudents };
  };

  const stats = getOverallStats();

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  if (studentPerformances.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Student Test Analysis
          </CardTitle>
          <CardDescription>
            No student test data available yet
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center py-8">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            Your students haven't completed any tests yet or you don't have any students assigned.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Student Test Analysis
          </CardTitle>
          <CardDescription>
            Track your students' test performance and progress
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Filter by student" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Students</SelectItem>
                {studentPerformances.map(student => (
                  <SelectItem key={student.student_email} value={student.student_email}>
                    {student.student_name || student.student_email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Overall Stats */}
          {selectedStudent === 'all' && stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Active Students</span>
                  </div>
                  <div className="text-2xl font-bold">{stats.activeStudents}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Total Tests</span>
                  </div>
                  <div className="text-2xl font-bold">{stats.totalTests}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Award className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Average Score</span>
                  </div>
                  <div className="text-2xl font-bold">{Math.round(stats.averageScore)}%</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-muted-foreground">Improving</span>
                  </div>
                  <div className="text-2xl font-bold text-green-600">{stats.improvingStudents}</div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Student Performance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredPerformances.map((performance) => (
          <Card key={performance.student_email}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="text-lg">
                  {performance.student_name || performance.student_email}
                </span>
                <div className="flex items-center gap-1">
                  {performance.improvement_trend === 'up' && (
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  )}
                  {performance.improvement_trend === 'down' && (
                    <TrendingDown className="h-4 w-4 text-red-500" />
                  )}
                  <Badge variant={
                    performance.average_score >= 80 ? 'default' :
                    performance.average_score >= 60 ? 'secondary' : 'destructive'
                  }>
                    {Math.round(performance.average_score)}% avg
                  </Badge>
                </div>
              </CardTitle>
              <CardDescription>
                {performance.total_tests} tests completed
                {performance.last_test_date && (
                  <span> • Last test: {new Date(performance.last_test_date).toLocaleDateString()}</span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Subjects */}
                <div>
                  <p className="text-sm font-medium mb-2">Subjects:</p>
                  <div className="flex flex-wrap gap-1">
                    {performance.subjects.length > 0 ? (
                      performance.subjects.map((subject, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {subject}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-xs text-muted-foreground">No subjects</span>
                    )}
                  </div>
                </div>

                {/* Recent Scores Trend */}
                {performance.recent_scores.length >= 3 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Recent Performance:</p>
                    <div className="h-32">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={performance.recent_scores.map((score, index) => ({
                          test: index + 1,
                          score: Math.round(score)
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="test" />
                          <YAxis domain={[0, 100]} />
                          <Tooltip 
                            formatter={(value) => [`${value}%`, 'Score']}
                            labelFormatter={(label) => `Test ${label}`}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="score" 
                            stroke="hsl(var(--primary))" 
                            strokeWidth={2}
                            dot={{ fill: "hsl(var(--primary))", strokeWidth: 2, r: 3 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}